import api from './api';

/**
 * Time Trial Service
 * Provides API integration for time trial management and standings
 */

export const timeTrialService = {
  // ==================== Admin Operations ====================
  
  /**
   * Create a new time trial
   * @param {Object} trialData - Time trial creation data
   * @returns {Promise<Object>} Created time trial
   */
  createTimeTrial: async (trialData) => {
    try {
      const response = await api.post('/api/gulag/time-trials/', trialData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating time trial:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create time trial'
      };
    }
  },
  
  /**
   * Update an existing time trial
   * @param {string} trialId - Time trial ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated time trial
   */
  updateTimeTrial: async (trialId, updateData) => {
    try {
      const response = await api.put(`/api/gulag/time-trials/${trialId}`, updateData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating time trial:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update time trial'
      };
    }
  },
  
  /**
   * Start/activate a time trial
   * @param {string} trialId - Time trial ID
   * @returns {Promise<Object>} Started time trial
   */
  startTimeTrial: async (trialId) => {
    try {
      const response = await api.post(`/api/gulag/time-trials/${trialId}/start`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error starting time trial:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to start time trial'
      };
    }
  },
  
  /**
   * Finalize time trial and award Gulag points
   * @param {string} trialId - Time trial ID
   * @returns {Promise<Object>} Finalization result with points awarded
   */
  finalizeTimeTrial: async (trialId) => {
    try {
      const response = await api.post(`/api/gulag/time-trials/${trialId}/finalize`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error finalizing time trial:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to finalize time trial'
      };
    }
  },
  
  /**
   * Delete a time trial
   * @param {string} trialId - Time trial ID
   * @returns {Promise<Object>} Deletion result
   */
  deleteTimeTrial: async (trialId) => {
    try {
      await api.delete(`/api/gulag/time-trials/${trialId}`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting time trial:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete time trial'
      };
    }
  },
  
  // ==================== Public Operations ====================
  
  /**
   * Get all time trials with optional filters
   * @param {string} eventId - Optional event ID filter
   * @param {string} status - Optional status filter (upcoming, active, finalized, cancelled)
   * @returns {Promise<Array>} List of time trials
   */
  getTimeTrials: async (eventId = null, status = null) => {
    try {
      const params = {};
      if (eventId) params.event_id = eventId;
      if (status) params.status = status;
      
      const response = await api.get('/api/gulag/time-trials/', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching time trials:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch time trials',
        data: []
      };
    }
  },
  
  /**
   * Get a specific time trial by ID
   * @param {string} trialId - Time trial ID
   * @returns {Promise<Object>} Time trial data
   */
  getTimeTrial: async (trialId) => {
    try {
      const response = await api.get(`/api/gulag/time-trials/${trialId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching time trial:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch time trial',
        data: null
      };
    }
  },
  
  /**
   * Get global standings for a time trial
   * @param {string} trialId - Time trial ID
   * @returns {Promise<Object>} Standings with driver rankings and points
   */
  getStandings: async (trialId) => {
    try {
      const response = await api.get(`/api/gulag/time-trials/${trialId}/standings`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching standings:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch standings',
        data: null
      };
    }
  },
  
  /**
   * Get rankings for a specific track within a time trial
   * @param {string} trialId - Time trial ID
   * @param {string} trackName - Track name
   * @returns {Promise<Object>} Track-specific rankings
   */
  getTrackRankings: async (trialId, trackName) => {
    try {
      const response = await api.get(
        `/api/gulag/time-trials/${trialId}/tracks/${encodeURIComponent(trackName)}`
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching track rankings for ${trackName}:`, error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch track rankings',
        data: null
      };
    }
  },
  
  // ==================== Driver Mapping Operations ====================
  
  /**
   * Get drivers with lap times for a time trial
   * @param {string} trialId - Time trial ID
   * @returns {Promise<Object>} Drivers list with lap counts and best times
   */
  getTrialDrivers: async (trialId) => {
    try {
      const response = await api.get(`/api/gulag/time-trials/${trialId}/drivers`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching trial drivers:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load drivers',
        data: []
      };
    }
  },
  
  /**
   * Get driver mappings for a time trial
   * @param {string} trialId - Time trial ID
   * @returns {Promise<Object>} Driver mappings
   */
  getDriverMappings: async (trialId) => {
    try {
      const response = await api.get(`/api/gulag/time-trials/${trialId}/mappings`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching driver mappings:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load mappings',
        data: []
      };
    }
  },
  
  /**
   * Create or update a driver mapping
   * @param {string} trialId - Time trial ID
   * @param {string} driverName - Driver name from lap times
   * @param {string} userId - User ID to map to
   * @returns {Promise<Object>} Mapping result
   */
  createDriverMapping: async (trialId, driverName, userId) => {
    try {
      const response = await api.post(`/api/gulag/time-trials/${trialId}/mappings`, {
        driver_name: driverName,
        user_id: userId
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating driver mapping:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create mapping'
      };
    }
  },
  
  /**
   * Delete a driver mapping
   * @param {string} trialId - Time trial ID
   * @param {string} driverName - Driver name to unmap
   * @returns {Promise<Object>} Deletion result
   */
  deleteDriverMapping: async (trialId, driverName) => {
    try {
      await api.delete(`/api/gulag/time-trials/${trialId}/mappings/${encodeURIComponent(driverName)}`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting driver mapping:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete mapping'
      };
    }
  },
  
  /**
   * Get event participants for mapping
   * @param {string} trialId - Time trial ID
   * @returns {Promise<Object>} Event participants
   */
  getEventParticipants: async (trialId) => {
    try {
      const response = await api.get(`/api/gulag/time-trials/${trialId}/participants`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching event participants:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load participants',
        data: []
      };
    }
  },
  
  // ==================== Utility Functions ====================
  
  /**
   * Format lap time from milliseconds to MM:SS.mmm
   * @param {number} lapTimeMs - Lap time in milliseconds
   * @returns {string} Formatted lap time
   */
  formatLapTime: (lapTimeMs) => {
    if (!lapTimeMs || lapTimeMs <= 0) return '--:--.---';
    
    const totalSeconds = lapTimeMs / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes}:${seconds.toFixed(3).padStart(6, '0')}`;
  },
  
  /**
   * Get status badge color
   * @param {string} status - Time trial status
   * @returns {string} MUI color name
   */
  getStatusColor: (status) => {
    switch (status) {
      case 'upcoming':
        return 'info';
      case 'active':
        return 'success';
      case 'finalized':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  },
  
  /**
   * Check if time trial is currently active
   * @param {Object} trial - Time trial object
   * @returns {boolean} True if trial is currently active
   */
  isActive: (trial) => {
    if (trial.status !== 'active') return false;
    
    const now = new Date();
    const start = new Date(trial.start_time);
    const end = new Date(trial.end_time);
    
    return now >= start && now <= end;
  }
};

export default timeTrialService;

